import type {
  AccountView,
  ApprovalView,
  BotView,
  Choice,
  DashboardView,
  DiffView,
  Field,
  KillSwitchView,
  LogLineView,
  Problem,
  RunView,
  SecretView,
  StartRun,
  TaskView,
} from "./types";

/**
 * Everything the front end asks the dashboard.
 *
 * One place, so a screen never builds a URL. Failures arrive as two kinds and only two: `LoggedOut`,
 * which the app turns into the login form, and `Refused`, which carries a sentence the server wrote
 * for a person to read.
 *
 * **Nothing here swallows an error.** The Kotlin this replaces discarded them in twelve places, and
 * that is why pressing Stop appeared to do nothing: the server answered "Run 41 is not running" and
 * the screen threw the sentence away. Callers may decide a failure is not worth showing; they may
 * not fail to notice one.
 */

/** The server said no, and said why. */
export class Refused extends Error {
  constructor(message: string) {
    super(message);
    this.name = "Refused";
  }
}

/** The session is gone, which sends the screen back to the login form. */
export class LoggedOut extends Error {
  constructor() {
    super("logged out");
    this.name = "LoggedOut";
  }
}

async function refusal(response: Response): Promise<never> {
  if (response.status === 401) throw new LoggedOut();

  // The server writes a sentence meant for a person; that sentence is what surfaces. A refusal with
  // no readable body would rather say nothing useful than show a status code.
  const said = await response
    .json()
    .then((body: Problem) => body.message)
    .catch(() => null);

  throw new Refused(said ?? "The dashboard could not do that.");
}

/**
 * Every request, with the browser's cache taken out of the loop.
 *
 * The server says `no-store` on everything under `/api`, and that is the real rule. This says it
 * again from the other side because a header cannot reach a cache that has already been poisoned: a
 * build went out that answered these paths with `max-age=31536000`, and a browser that stored one
 * of those will not ask again for a year -- no deploy, no reload and no server change reaches it,
 * because no request is made. `no-store` here bypasses the stored copy, so the first load of any
 * newer build repairs itself.
 */
function asked(init?: RequestInit): RequestInit {
  return { ...init, cache: "no-store" };
}

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, asked(init));
  if (!response.ok) await refusal(response);
  return (await response.json()) as T;
}

async function nothing(path: string, init?: RequestInit): Promise<void> {
  const response = await fetch(path, asked(init));
  if (!response.ok) await refusal(response);
}

function sending(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export const api = {
  /** Whether a session is open. A network failure is not an answer, so it throws. */
  async session(): Promise<boolean> {
    const response = await fetch("/api/session", asked());
    if (response.status === 401) return false;
    if (!response.ok) await refusal(response);
    return true;
  },

  logIn(password: string): Promise<void> {
    return nothing("/api/login", sending({ password }));
  },

  /** Surfaced rather than ignored: a logout that failed left a session open. */
  logOut(): Promise<void> {
    return nothing("/api/logout", { method: "POST" });
  },

  dashboard(): Promise<DashboardView> {
    return json<DashboardView>("/api/dashboard");
  },

  bots(): Promise<BotView[]> {
    return json<BotView[]>("/api/bots");
  },

  /** The accounts a bot can be told to edit as, which is whatever the environment declares. */
  accounts(): Promise<AccountView[]> {
    return json<AccountView[]>("/api/accounts");
  },

  /** Points a bot at an account, or back at the one they share when the set is null. */
  pointAt(botId: string, set: string | null): Promise<void> {
    const path = `/api/bots/${encodeURIComponent(botId)}/account`;
    return nothing(path, { ...sending({ set }), method: "PUT" });
  },

  /** What each secret the dashboard can be told holds: its length and when, never the secret. */
  secrets(): Promise<SecretView[]> {
    return json<SecretView[]>("/api/secrets");
  },

  /** Sends a secret, and is answered with what it now holds rather than with what was sent. */
  setSecret(name: string, value: string): Promise<SecretView> {
    return json<SecretView>(`/api/secrets/${encodeURIComponent(name)}`, {
      ...sending({ value }),
      method: "PUT",
    });
  },

  /** Forgets a secret, which puts its environment variable back in charge of it. */
  clearSecret(name: string): Promise<SecretView> {
    return json<SecretView>(`/api/secrets/${encodeURIComponent(name)}`, {
      method: "DELETE",
    });
  },

  /** Encoded, because a bot id or a field name is not guaranteed to be URL-safe. */
  choices(botId: string, field: string): Promise<Choice[]> {
    const path = `/api/bots/${encodeURIComponent(botId)}/choices/${encodeURIComponent(field)}`;
    return json<Choice[]>(path);
  },

  tasks(botId?: string): Promise<TaskView[]> {
    const query =
      botId === undefined ? "" : `?bot=${encodeURIComponent(botId)}`;
    return json<TaskView[]>(`/api/tasks${query}`);
  },

  addTask(task: TaskView): Promise<TaskView> {
    return json<TaskView>("/api/tasks", sending(task));
  },

  updateTask(task: TaskView): Promise<TaskView> {
    return json<TaskView>(`/api/tasks/${task.id ?? 0}`, {
      ...sending(task),
      method: "PUT",
    });
  },

  deleteTask(id: number): Promise<void> {
    return nothing(`/api/tasks/${id}`, { method: "DELETE" });
  },

  /**
   * One run, with every step it was made of.
   *
   * The list endpoint carries only what a row needs; the steps are asked for here because a run of
   * thirty-six languages should not put thirty-six of them in a list of fifty runs.
   */
  run(id: number): Promise<RunView> {
    return json<RunView>(`/api/runs/${id}`);
  },

  runs(botId?: string): Promise<RunView[]> {
    const query =
      botId === undefined ? "" : `?bot=${encodeURIComponent(botId)}`;
    return json<RunView[]>(`/api/runs${query}`);
  },

  log(id: number, after: number): Promise<LogLineView[]> {
    return json<LogLineView[]>(`/api/runs/${id}/log?after=${after}`);
  },

  /**
   * A sample of what a run changed, or would have: its first diff, saved or not.
   *
   * `null` means the run recorded no diff, which is an answer rather than a failure: it changed nothing a
   * diff can show. A real failure throws, so the two are no longer indistinguishable.
   */
  async diff(id: number): Promise<DiffView | null> {
    const response = await fetch(`/api/runs/${id}/diff`, asked());
    if (response.status === 204) return null;
    if (!response.ok) await refusal(response);
    return (await response.json()) as DiffView;
  },

  start(request: StartRun): Promise<RunView> {
    return json<RunView>("/api/runs", sending(request));
  },

  cancel(id: number): Promise<void> {
    return nothing(`/api/runs/${id}/cancel`, { method: "POST" });
  },

  decide(approval: ApprovalView, accept: boolean): Promise<void> {
    return nothing("/api/approval", sending({ id: approval.id, accept }));
  },
};

/*
 * Three things the Kotlin computed and the wire does not carry. They are properties on the Kotlin
 * types, so nothing sends them; the generator is right to leave them out and they belong here.
 */

/** Whether anything may save right now. */
export function clear(killSwitch: KillSwitchView): boolean {
  return killSwitch.reachable && !killSwitch.stopped;
}

/** A multi-pick with no choices of its own is one only the bot can fill in. */
export function fromBot(field: Field): boolean {
  return field.type === "multipick" && (field.choices ?? []).length === 0;
}

/** A run or an approval carries the bot's name, defaulting to its id. */
export function botName(of: { botId: string; botName?: string }): string {
  return of.botName ?? of.botId;
}
